// One-click trust stance (vector-trust phase 2, DECISIONS.md 2026-07-26):
// the SHARED affordance for "I trust / distrust X" — X an actor OR a claim.
// One component on every surface (the EdgeSentence precedent), because the
// 2026-07-26 finding was that the actor buttons existed but were unreachable:
// cheap to execute is not cheap to reach.
//
// No new mechanism: mints a `trusts` relation instance from YOUR actor object
// to the target (stance = edge polarity, the ruling's general sign
// convention) and casts your +1 vote to activate it. Same stance again
// withdraws; opposite stance replaces. For claims the stake reads back on
// the SOCIAL axis — never the epistemic band (axis separation is the
// parallel-objection protection; a distrust stake cannot counterfeit a
// contradiction).

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import { Explain } from './Explain';

let trustsRelationId = null; // module cache — the type id never changes
async function getTrustsId() {
  if (trustsRelationId) return trustsRelationId;
  const res = await apiFetch('/api/relations');
  const list = await res.json();
  trustsRelationId = list.find((r) => r.name === 'trusts')?.id || null;
  return trustsRelationId;
}

// `preloaded` (controlled mode, for list surfaces like the resolve stream):
// pass the viewer's stake edge ({ id, polarity, my_vote }) or null — the
// component then makes NO self-fetch (a stream of rows would be a request
// storm; the stream payload batches my_trust instead) and relies on
// onChanged to refresh the parent after an action.
export default function TrustStance({ targetId, kind = 'claim', compact = false, onChanged, preloaded }) {
  const { user } = useAuth();
  const myActorId = user?.object_id;
  const controlled = preloaded !== undefined;
  const [myEdgeState, setMyEdge] = useState(null);
  const [busy, setBusy] = useState(false);
  const myEdge = controlled ? preloaded : myEdgeState;

  const load = useCallback(async () => {
    if (!myActorId || controlled) return;
    try {
      const res = await apiFetch(`/api/relation-instances/${targetId}`);
      const rels = await res.json();
      setMyEdge(
        rels.find(
          (r) =>
            r.relation_name === 'trusts' &&
            r.source_id === myActorId &&
            r.target_id === targetId &&
            !r.is_system
        ) || null
      );
    } catch {
      // stance stays unknown; buttons still allow a first stake
    }
  }, [targetId, myActorId, controlled]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user || !myActorId || targetId === myActorId) return null;

  const stance = async (polarity) => {
    if (busy) return;
    setBusy(true);
    try {
      const removeMine = async () => {
        // vote deletion is skipped when my_vote is unknown (controlled mode):
        // votes CASCADE with the edge, so deleting the edge is sufficient
        if (myEdge.my_vote != null) await apiFetch(`/api/votes/${myEdge.id}`, { method: 'DELETE' });
        await apiFetch(`/api/relation-instances/${myEdge.id}`, { method: 'DELETE' });
      };
      if (myEdge && myEdge.polarity === polarity) {
        await removeMine(); // same stance again = withdraw entirely
      } else {
        if (myEdge) await removeMine(); // switching stance replaces the edge
        const trustsId = await getTrustsId();
        if (!trustsId) throw new Error('trusts relation type not found');
        const res = await apiFetch('/api/relation-instances', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            relation_id: trustsId,
            source_id: myActorId,
            target_id: targetId,
            polarity,
          }),
        });
        if (!res.ok) throw new Error('Failed to create trust edge');
        const edge = await res.json();
        // your +1 vote activates your own assertion (unvoted edges are inert)
        await apiFetch('/api/votes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ relation_instance_id: edge.id, polarity: 1 }),
        });
      }
      await load();
      onChanged?.();
    } catch (err) {
      console.error('Trust stance failed:', err);
    } finally {
      setBusy(false);
    }
  };

  const explainText =
    kind === 'actor'
      ? "This creates a 'trusts' connection from your identity to this actor (distrust is the same edge with negative polarity) and casts your vote to activate it — the same votable edge as everything else in the graph. Others can agree or disagree with your assessment, and the trust engines walk it."
      : "This stakes a 'trusts' connection from your identity to this claim and casts your vote to activate it — a personal credence stake on the claim's SOCIAL axis, shown beside the evidence, never mixed into it. It cannot stand in for a contradiction: to contest the claim itself, connect a contradicting claim and vote that edge.";

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 'var(--wv-sp-2)' : 'var(--wv-sp-3)', flexWrap: 'wrap' }}>
      <Explain title="Trust stance" text={explainText}>
        <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>your stance:</span>
      </Explain>
      {[[1, 'trust'], [-1, 'distrust']].map(([pol, label]) => {
        const active = myEdge?.polarity === pol;
        return (
          <button
            key={label}
            onClick={() => stance(pol)}
            disabled={busy}
            title={active ? 'Click again to withdraw your stance' : `Assert that you ${label} this ${kind}`}
            style={{
              padding: compact ? 'var(--wv-sp-0) var(--wv-sp-4)' : 'var(--wv-sp-1) var(--wv-sp-6)',
              fontSize: compact ? 'var(--wv-fs-sm)' : 'var(--wv-fs-md)',
              cursor: 'pointer',
              border: '1px solid ' + (pol === 1 ? 'var(--wv-pos)' : 'var(--wv-neg)'),
              borderRadius: 'var(--wv-r-pill)',
              background: active ? (pol === 1 ? 'var(--wv-pos)' : 'var(--wv-neg)') : 'none',
              color: active ? 'var(--wv-bg)' : pol === 1 ? 'var(--wv-pos)' : 'var(--wv-neg)',
            }}
          >
            {active ? '✓ ' : ''}{label}
            {/* withdraw made visible, same idiom as EdgeSentence's vote pill:
                the tooltip was the only sign that clicking again withdraws */}
            {active && <span aria-hidden="true" style={{ marginLeft: 'var(--wv-sp-1)', opacity: 0.75 }}>×</span>}
          </button>
        );
      })}
      {!compact && myEdge && (
        <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
          yours is a votable edge like any other — see Connections
        </span>
      )}
    </span>
  );
}
